import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';

export default function Terms() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <div className="bg-gradient-to-br from-purple-600 via-pink-600 to-purple-700 text-white py-10 sm:py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-3xl sm:text-4xl font-extrabold mb-3">Terms &amp; Conditions</h1>
          <p className="text-purple-100 text-sm sm:text-base max-w-xl mx-auto">
            Please read carefully before using our services. By placing an order, you accept the terms described below.
          </p>
          <p className="text-purple-200 text-xs mt-4">Last updated: May 2026</p>
        </div>
      </div>

      <main className="container mx-auto px-4 py-10 sm:py-16 max-w-4xl">

        <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 mb-10">
          <h2 className="font-bold text-gray-800 mb-4 text-sm uppercase tracking-wide">Table of Contents</h2>
          <ol className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-sm text-purple-700 list-decimal list-inside">
            <li>Scope and Definitions</li>
            <li>Company Information</li>
            <li>Services</li>
            <li>Order Process</li>
            <li>Obligations of GrowProPlus</li>
            <li>Obligations of the Customer</li>
            <li>Delivery Times</li>
            <li>Prices and Payment</li>
            <li>Refund Policy</li>
            <li>Guarantee and Replenishment</li>
            <li>Limitation of Liability</li>
            <li>Service Modifications</li>
            <li>Privacy and Personal Data</li>
            <li>Account Security</li>
            <li>Governing Law</li>
            <li>Contact</li>
          </ol>
        </div>

        <div className="space-y-10 text-gray-700 text-sm sm:text-base leading-relaxed">

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold px-2 py-1 rounded-full">1</span>
              Scope and Definitions
            </h2>
            <p className="mb-3">
              These Terms and Conditions govern the use of the website <strong>growproplus.com</strong> and all services provided by <strong>GrowProPlus</strong>. By accessing the website or placing an order, you fully accept these terms.
            </p>
            <p>
              <strong>"Services"</strong> refers to the social media growth services offered (followers, likes, views) for platforms including Instagram, TikTok, YouTube, and Facebook. <strong>"Customer"</strong> refers to any natural or legal person who places an order.
            </p>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold px-2 py-1 rounded-full">2</span>
              Company Information
            </h2>
            <p>
              <strong>GrowProPlus</strong> is a digital growth services provider operating at <strong>growproplus.com</strong>. For any enquiries, please contact us at <a href="mailto:growproplus@growproplus.com" className="text-pink-600 hover:underline">growproplus@growproplus.com</a>.
            </p>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold px-2 py-1 rounded-full">3</span>
              Services
            </h2>
            <p className="mb-3">
              GrowProPlus provides social media growth services including followers, likes, and views for various platforms. All services are delivered digitally and electronically.
            </p>
            <p>
              The services are designed to increase metrics on the specified social media accounts. Results are delivered gradually and naturally to ensure account safety.
            </p>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold px-2 py-1 rounded-full">4</span>
              Order Process
            </h2>
            <p className="mb-3">
              To place an order: (1) select the platform and service; (2) enter your username or profile link; (3) choose a package; (4) provide your details and complete payment.
            </p>
            <p>
              Payment is processed securely via Stripe. An order confirmation email will be sent after payment is received. No account registration is required.
            </p>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold px-2 py-1 rounded-full">5</span>
              Obligations of GrowProPlus
            </h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Process orders within the stated delivery timeframes.</li>
              <li>Provide customer support via email.</li>
              <li>Offer replenishment if metrics drop within the guarantee period.</li>
              <li>Protect customer data in accordance with our Privacy Policy.</li>
              <li>Process refunds in accordance with our Refund Policy.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold px-2 py-1 rounded-full">6</span>
              Obligations of the Customer
            </h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Provide accurate and up-to-date information when placing an order.</li>
              <li>Keep the profile or channel <strong>public</strong> throughout the delivery period.</li>
              <li>Not change the username or privacy settings during delivery.</li>
              <li>Use the services in compliance with the terms of the relevant social media platforms.</li>
              <li>Not request a chargeback without first contacting GrowProPlus support.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold px-2 py-1 rounded-full">7</span>
              Delivery Times
            </h2>
            <p className="mb-3">
              Delivery begins within <strong>24–72 hours</strong> of payment confirmation. Results appear gradually to ensure a natural delivery pattern. Actual delivery times may vary depending on the platform and package size.
            </p>
            <p>
              Delivery may be delayed if the profile is private, the username has changed, or the account has been suspended. GrowProPlus is not responsible for delays caused by changes on the customer's side.
            </p>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold px-2 py-1 rounded-full">8</span>
              Prices and Payment
            </h2>
            <p className="mb-3">
              All prices displayed on the website are in Euros (€) and are final. Payment is processed exclusively via Stripe (credit/debit card). No additional fees are charged by GrowProPlus.
            </p>
            <p>
              Prices may be updated at any time. The price applicable to an order is that displayed at the time of purchase.
            </p>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold px-2 py-1 rounded-full">9</span>
              Refund Policy
            </h2>
            <p>
              Please refer to our <Link to="/refund" className="text-pink-600 hover:underline font-semibold">Refund Policy</Link> for full details on refund conditions.
            </p>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold px-2 py-1 rounded-full">10</span>
              Guarantee and Replenishment
            </h2>
            <p className="mb-3">
              GrowProPlus offers a <strong>30-day replenishment guarantee</strong> on followers. If the follower count drops below the delivered amount within this period (due to natural platform fluctuations), we will replenish the difference free of charge.
            </p>
            <p>
              The guarantee does not apply if the account was made private, the username was changed, or the account was suspended or deleted.
            </p>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold px-2 py-1 rounded-full">11</span>
              Limitation of Liability
            </h2>
            <p className="mb-3">
              GrowProPlus is not responsible for any account suspensions, bans, or penalties imposed by social media platforms as a result of using our services. The customer uses the services at their own risk.
            </p>
            <p>
              Our liability is limited to the amount paid for the specific order in question.
            </p>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold px-2 py-1 rounded-full">12</span>
              Service Modifications
            </h2>
            <p>
              GrowProPlus reserves the right to modify, suspend, or discontinue any service at any time. In the event of a significant change affecting an active order, the customer will be notified and offered an alternative or refund.
            </p>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold px-2 py-1 rounded-full">13</span>
              Privacy and Personal Data
            </h2>
            <p>
              We collect only the minimum data necessary to process your order (name, email, phone). We do not sell or share your data with third parties. Payment data is handled exclusively by Stripe and is never stored on our servers. For more information, please contact <a href="mailto:growproplus@growproplus.com" className="text-pink-600 hover:underline">growproplus@growproplus.com</a>.
            </p>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold px-2 py-1 rounded-full">14</span>
              Account Security
            </h2>
            <p>
              GrowProPlus will <strong>never</strong> ask for your social media account password or login credentials. Our services do not require any access to your account. If anyone claims to be from GrowProPlus and asks for your password, do not provide it and report the incident to us immediately.
            </p>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold px-2 py-1 rounded-full">15</span>
              Governing Law
            </h2>
            <p>
              These Terms and Conditions shall be governed by and construed in accordance with applicable law. Any disputes shall be subject to the exclusive jurisdiction of the competent courts.
            </p>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold px-2 py-1 rounded-full">16</span>
              Contact
            </h2>
            <p>
              For any questions about these Terms, please contact us at:
              <br />
              📧 <a href="mailto:growproplus@growproplus.com" className="text-pink-600 hover:underline font-semibold">growproplus@growproplus.com</a>
              <br />
              🌐 <a href="https://growproplus.com" className="text-pink-600 hover:underline">growproplus.com</a>
            </p>
          </section>

        </div>
      </main>

      <footer className="bg-gray-900 text-white">
        <div className="container mx-auto px-4 py-8 text-center">
          <p className="text-gray-400 text-sm">© 2026 GrowProPlus. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
